import type {
    SourceSpecificationUnion,
    VectorSourceSpecification,
    RasterSourceSpecification,
    RasterDemSourceSpecification,
    GeoJSONSourceSpecification,
    VideoSourceSpecification,
    ImageSourceSpecification,
} from './types';

// Interfaccia per mappare i tipi di sorgente alle loro chiavi valide
interface ValidSourcePropertiesMap {
    vector: Array<keyof VectorSourceSpecification>;
    raster: Array<keyof RasterSourceSpecification>;
    'raster-dem': Array<keyof RasterDemSourceSpecification>;
    geojson: Array<keyof GeoJSONSourceSpecification>;
    video: Array<keyof VideoSourceSpecification>;
    image: Array<keyof ImageSourceSpecification>;
}

const VALID_SOURCE_PROPERTIES: ValidSourcePropertiesMap = {
    vector: ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'promoteId', 'tileSize', 'attribution', 'minzoom', 'maxzoom'],
    raster: ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'tileSize', 'attribution', 'minzoom', 'maxzoom'],
    'raster-dem': ['type', 'url', 'tiles', 'bounds', 'scheme', 'volatile', 'tileSize', 'encoding', 'redFactor', 'greenFactor', 'blueFactor', 'baseShift', 'attribution', 'minzoom', 'maxzoom'],
    geojson: ['type', 'data', 'maxzoom', 'buffer', 'filter', 'tolerance', 'cluster', 'clusterRadius', 'clusterMaxZoom', 'clusterMinPoints', 'clusterProperties', 'lineMetrics', 'generateId', 'promoteId', 'attribution' /* minzoom rimosso apposta */],
    video: ['type', 'urls', 'coordinates'],
    image: ['type', 'url', 'coordinates'],
};

/**
 * Pulisce le opzioni della sorgente MapLibre, rimuovendo le proprietà non valide o
 * specifiche dell'UI che non dovrebbero essere passate direttamente a MapLibre.
 * @param sourceSpec Le opzioni della sorgente da pulire.
 * @returns Un oggetto contenente solo le proprietà valide per il tipo di sorgente specificato.
 */
export function cleanSourceOptions(sourceSpec: SourceSpecificationUnion): Record<string, any> {
    const cleanedOptions: Record<string, any> = {};
    const sourceType = sourceSpec.type;

    if (!sourceType || !(sourceType in VALID_SOURCE_PROPERTIES)) {
        console.error(`[store/utils/cleanSourceOptions] Tipo di sorgente non valido o mancante: ${sourceType}`);
        // Ritorna un oggetto con il tipo, anche se sconosciuto, per evitare errori a cascata
        return { type: sourceType || 'unknown' };
    }

    const validKeys = VALID_SOURCE_PROPERTIES[sourceType];

    for (const key of validKeys) {
        if (Object.prototype.hasOwnProperty.call(sourceSpec, key)) {
            const value = (sourceSpec as any)[key];
            // Includi la proprietà solo se è definita (non undefined)
            // Questo permette di passare null o false se sono valori validi per MapLibre
            if (value !== undefined) {
                cleanedOptions[key as string] = value;
            }
        }
    }
    // Assicura che 'type' sia sempre presente se il sourceType è valido
    if (!cleanedOptions.type && sourceType && (sourceType in VALID_SOURCE_PROPERTIES)) {
         cleanedOptions.type = sourceType;
    }

    // Logica specifica per geojson: rimuovi minzoom se presente, perché non è una proprietà standard
    // per le sorgenti geojson in MapLibre GL JS (anche se a volte usata per il clustering).
    // Se si vuole controllare il minzoom di un layer geojson, si usa la proprietà 'minzoom' del layer, non della sorgente.
    if (cleanedOptions.type === 'geojson' && cleanedOptions.hasOwnProperty('minzoom')) {
        delete cleanedOptions.minzoom;
    }
    return cleanedOptions;
}